import Axios from 'axios';
import { getBffBaseUrl } from '../../helper';

export type SistemaLead = 'monolito' | 'bff';

/**
 * Contrato de `POST /venta-presencial/v1/leads` del BFF (US 23444): registra qué
 * vendedor y en qué sucursal trajo un lead que creó otro sistema. Anónimo, con
 * límite de pedidos por IP; repetir el mismo `sistema` + `leadRef` no es error.
 */
export interface RegistrarLeadBody {
  origen: string;
  sistema: SistemaLead;
  leadRef: string;
  vendedor: string;
  /** Código de la sucursal elegida. Sin él (origen sin lista cargada) el lead queda sin sucursal. */
  sucursal?: string;
  landingUrl?: string;
}

export const registrarLeadVentaPresencial = async (body: RegistrarLeadBody): Promise<void> => {
  const baseUrl = await getBffBaseUrl();
  await Axios.post(baseUrl + '/venta-presencial/v1/leads', body);
};

/** Sucursal del origen: el código es lo que se registra; el nombre, lo que ve el asesor. */
export interface Sucursal {
  codigo: string;
  nombre: string;
}

/**
 * Acepta el formato definitivo (`{ codigo, nombre }`) y el anterior (solo el texto,
 * que hace de código y de nombre). Descarta lo que no trae código.
 */
const aSucursal = (crudo: unknown): Sucursal | null => {
  if (typeof crudo === 'string') {
    const texto = crudo.trim();
    return texto ? { codigo: texto, nombre: texto } : null;
  }
  if (!crudo || typeof crudo !== 'object') return null;
  const { codigo, nombre } = crudo as { codigo?: unknown; nombre?: unknown };
  const cod = typeof codigo === 'string' ? codigo.trim() : '';
  if (!cod) return null;
  const nom = typeof nombre === 'string' ? nombre.trim() : '';
  return { codigo: cod, nombre: nom || cod };
};

/**
 * Sucursales del origen para el desplegable de la pantalla del vendedor (BFF, US 23445):
 * `GET /venta-presencial/v1/origenes/{origen}/sucursales`. Anónimo; 404 si el origen
 * no existe o está inactivo. Respeta el orden configurado. Una lista vacía significa
 * que el origen todavía no tiene sucursales cargadas.
 */
export const obtenerSucursales = async (origen: string): Promise<Sucursal[]> => {
  const baseUrl = await getBffBaseUrl();
  const respuesta = await Axios.get<{ origen: string; sucursales?: unknown[] }>(
    `${baseUrl}/venta-presencial/v1/origenes/${encodeURIComponent(origen)}/sucursales`
  );
  const lista = respuesta.data?.sucursales;
  if (!Array.isArray(lista)) return [];
  // Un código repetido (error de carga en el BFF) dejaría dos opciones con el mismo valor: queda la primera.
  const porCodigo = new Map<string, Sucursal>();
  for (const sucursal of lista.map(aSucursal)) {
    if (sucursal && !porCodigo.has(sucursal.codigo)) porCodigo.set(sucursal.codigo, sucursal);
  }
  return Array.from(porCodigo.values());
};
